import React from 'react';

import { Icon, RyeconLinkExternal } from '@dbt-labs/sourdough';

interface DataMonitorFooterProps {
  accountId: number | null | undefined;
  projectId: number | null | undefined;
  environmentId: number | null | undefined;
  uniqueId: string | null | undefined;
  environmentType: 'production' | 'staging' | 'development' | null | undefined;
}

const generateURL = ({
  accountId,
  projectId,
  environmentId,
  uniqueId,
  environmentType,
}: DataMonitorFooterProps): string | null => {
  if (!accountId || !projectId || !environmentId || !uniqueId) return null;
  // multi cell url scheme https://yh000.metadata.****** doesn't match original replace pattern https://metadata.*****
  const cloudBaseUrl = window.location.href
    .replace('//metadata.', '//')
    .replace('.metadata.', '.')
    .replace('codex-api-gateway-', '') // for devspace: codex-api-gateway-dev-eddowh.dev.dbt.com/exposure-tile -> dev-eddowh.dev.dbt.com/explore
    .split('/exposure-tile')[0];
  const explorerBaseUrl =
    import.meta.env.MODE === 'development'
      ? (import.meta.env.VITE_EXPLORER_BASE_URL ?? cloudBaseUrl)
      : cloudBaseUrl;
  return `${explorerBaseUrl}/explore/${accountId}/projects/${projectId}/environments/${environmentType}/details/${uniqueId}`;
};

const DataMonitorFooter: React.FC<DataMonitorFooterProps> = (props) => {
  const detailsUrl = generateURL(props);

  return (
    <div className="border-t-2 border-borderMuted bg-bgBackground">
      <div className="flex px-7 py-6">
        <span className="flex-1">
          <svg
            width="56"
            height="22"
            viewBox="0 0 48 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M31.3814 2.78302V15.0705H28.8464V13.8174C28.5813 14.2568 28.2002 14.6148 27.7363 14.8427C27.2392 15.0868 26.6759 15.217 26.1125 15.217C25.4166 15.2333 24.7207 15.038 24.1408 14.6474C23.5443 14.2568 23.0804 13.7197 22.7822 13.085C22.4508 12.3526 22.2851 11.5714 22.3017 10.774C22.2851 9.97651 22.4508 9.19532 22.7822 8.47922C23.0804 7.86078 23.5443 7.32371 24.1242 6.94939C24.7207 6.57506 25.4166 6.39604 26.1125 6.41232C26.6593 6.41232 27.2061 6.52624 27.6866 6.77036C28.1505 6.99821 28.5316 7.33998 28.7967 7.76313V3.61304L31.3814 2.78302ZM28.3327 12.6456C28.6641 12.2224 28.8464 11.604 28.8464 10.8065C28.8464 10.0091 28.6807 9.40689 28.3327 8.98374C27.9848 8.5606 27.438 8.31647 26.8747 8.33275C26.3113 8.3002 25.7646 8.54432 25.4 8.96747C25.0521 9.39062 24.8698 9.99278 24.8698 10.774C24.8698 11.5714 25.0521 12.1899 25.4 12.6293C25.748 13.0687 26.3113 13.3129 26.8747 13.2966C27.5043 13.2966 28.0014 13.0687 28.3327 12.6456Z"
              fill="#1F2129"
            />
            <path
              d="M40.4447 6.93311C41.0411 7.30743 41.5051 7.8445 41.7867 8.46295C42.1181 9.17904 42.2838 9.96023 42.2672 10.7577C42.2838 11.5552 42.1181 12.3526 41.7867 13.0687C41.4885 13.7035 41.0246 14.2405 40.4281 14.6311C39.8482 15.0054 39.1523 15.2007 38.4564 15.1845C37.8931 15.2007 37.3297 15.0705 36.8327 14.8101C36.3687 14.5823 35.9876 14.2242 35.7391 13.7848V15.0543H33.1875V3.61304L35.7557 2.78302V7.79568C36.0042 7.35626 36.3853 6.99821 36.8492 6.77036C37.3463 6.50996 37.8931 6.37976 38.4564 6.39604C39.1523 6.37976 39.8482 6.57506 40.4447 6.93311ZM39.1854 12.6293C39.5334 12.1899 39.6991 11.5714 39.6991 10.7577C39.6991 9.97651 39.5334 9.37434 39.1854 8.95119C38.8375 8.52805 38.357 8.31647 37.7274 8.31647C37.164 8.28392 36.6173 8.52805 36.2527 8.96747C35.9048 9.40689 35.7391 10.0091 35.7391 10.7903C35.7391 11.5877 35.9048 12.2062 36.2527 12.6293C36.6007 13.0687 37.1475 13.3129 37.7274 13.2803C38.2907 13.3129 38.8375 13.0687 39.1854 12.6293Z"
              fill="#1F2129"
            />
            <path
              d="M45.8129 8.49551V11.783C45.8129 12.255 45.9455 12.5968 46.1774 12.8083C46.4425 13.0362 46.7905 13.1501 47.155 13.1338C47.4367 13.1338 47.7349 13.085 48 13.0199V14.9729C47.5195 15.1357 47.0224 15.217 46.5254 15.2008C45.465 15.2008 44.6531 14.9078 44.0898 14.3219C43.5264 13.736 43.2448 12.906 43.2448 11.8319V4.62209L45.8129 3.80835V6.60763H48V8.49551H45.8129V8.49551Z"
              fill="#1F2129"
            />
            <path
              d="M17.5464 0.748644C17.9772 1.15552 18.2589 1.69259 18.3252 2.27848C18.3252 2.5226 18.2589 2.68535 18.1098 2.99458C17.9607 3.3038 16.1215 6.42857 15.5747 7.29114C15.2599 7.79566 15.0942 8.39783 15.0942 8.98373C15.0942 9.5859 15.2599 10.1718 15.5747 10.6763C16.1215 11.5389 17.9607 14.6799 18.1098 14.9892C18.2589 15.2984 18.3252 15.4448 18.3252 15.689C18.2589 16.2749 17.9938 16.8119 17.563 17.2025C17.1488 17.6257 16.602 17.9024 16.0221 17.9512C15.7736 17.9512 15.6079 17.8861 15.3096 17.7396C15.0114 17.5931 11.7639 15.8354 10.8857 15.2984C10.8195 15.2658 10.7532 15.217 10.6704 15.1844L6.32931 12.6618C6.42872 13.4756 6.79324 14.2568 7.38972 14.8264C7.5057 14.9403 7.62168 15.038 7.75423 15.1356C7.65482 15.1844 7.53884 15.2333 7.43942 15.2984C6.56127 15.8354 3.36348 17.642 3.04867 17.7884C2.73386 17.9349 2.58474 18 2.31964 18C1.72316 17.9349 1.17639 17.6745 0.778737 17.2514C0.347946 16.8445 0.0662755 16.3074 0 15.7215C0.0165689 15.4774 0.0828443 15.2333 0.215395 15.0217C0.364515 14.7125 2.20366 11.5714 2.75043 10.7089C3.06524 10.2043 3.23093 9.61845 3.23093 9.01628C3.23093 8.41411 3.06524 7.82821 2.75043 7.32369C2.20366 6.42857 0.347946 3.28752 0.215395 2.9783C0.0828443 2.76673 0.0165689 2.5226 0 2.27848C0.0662755 1.69259 0.331377 1.15552 0.762168 0.748644C1.17639 0.325497 1.72316 0.0650995 2.31964 0C2.56817 0.0162749 2.81671 0.0813743 3.04867 0.211573C3.31377 0.325497 5.64998 1.66004 6.89265 2.37613L7.17432 2.53888C7.27373 2.60398 7.35658 2.6528 7.42285 2.68535L7.5554 2.76673L11.9793 5.33816C11.8799 4.36166 11.3662 3.46655 10.5709 2.86438C10.6704 2.81555 10.7863 2.76673 10.8857 2.70163C11.7639 2.16456 14.9617 0.341772 15.2765 0.211573C15.4919 0.0813743 15.7404 0.0162749 16.0055 0C16.5854 0.0650995 17.1322 0.325497 17.5464 0.748644ZM9.39455 10.1555L10.339 9.22785C10.4715 9.09765 10.4715 8.90235 10.339 8.77215L9.39455 7.84449C9.262 7.71429 9.06317 7.71429 8.93062 7.84449L7.9862 8.77215C7.85364 8.90235 7.85364 9.09765 7.9862 9.22785L8.93062 10.1555C9.0466 10.2694 9.262 10.2694 9.39455 10.1555Z"
              fill="#FF694A"
            />
          </svg>
        </span>
        {detailsUrl && (
          <span className="flex-0 font-medium text-fgBrand">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={detailsUrl}
              className="space-x-1.5"
            >
              <Icon ryecon={RyeconLinkExternal} />
              <span>See more details</span>
            </a>
          </span>
        )}
      </div>
    </div>
  );
};

export default DataMonitorFooter;
